import { useCallback, useEffect, useState, useContext } from "react";
import { FilterContext } from "../FilterContextProvider";
import useApi from "../Api/Api";

const useEvents = (filterOverrides = {}, vendorFilter = {}, ctmTaxable = false, usePagination = true, totalPayroll = false) => {
  const { filterData } = useContext(FilterContext);
  const api = useApi();
  const [events, setEvents] = useState([]);
  const [pagy, setPagy] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortFilter, setSortFilter] = useState("date");
  const [searchFilter, setSearchFilter] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [eventIds, setEventIds] = useState([]);
  const [eventIdsQueryString, setEventIdsQueryString] = useState("");
  const [remittedFilter, setRemittedFilter] = useState();
  const [reportedFilter, setReportedFilter] = useState();
  const [eventDates, setEventDates] = useState({
    startDate: "",
    endDate: "",
  });

  const getEvents = useCallback(
    (page, usePaginationOverride = usePagination, skipSetEvents = false) => {
      setIsLoading(true);
      let queryParams = { page };

      if (filterData.artist_id) {
        queryParams.artist_id = filterData.artist_id;
      }
      if (eventDates.startDate) {
        queryParams.start_date = eventDates.startDate;
      }
      if (ctmTaxable === true) {
        queryParams.ctm_tax_responsible = true;
      }
      if (totalPayroll === true) {
        queryParams.total_payroll = true;
      }
      if (eventDates.endDate) {
        queryParams.end_date = eventDates.endDate;
      }
      if (remittedFilter != null) {
        queryParams.remitted = remittedFilter;
      }
      if (reportedFilter != null) {
        queryParams.reported = reportedFilter;
      }
      if (sortFilter) {
        queryParams.sort = sortFilter;
      }
      if (searchFilter) {
        queryParams.query = searchFilter;
      }
      if (filterOverrides) {
        queryParams.reporting_type = filterOverrides;
      }
      if (vendorFilter) {
        queryParams.vendor = vendorFilter;
      }
      let queryString = Object.keys(queryParams)
        .map((param) => `${param}=${queryParams[param]}`)
        .join("&");
      if (eventIdsQueryString) {
        queryString = queryString + eventIdsQueryString;
      }
      const shouldFetch = filterData.artist_id || totalPayroll;
      if (usePaginationOverride) {
        return (
          shouldFetch &&
          api.get(`/events?${queryString}`).then((r) => {
            setEvents(r.data.data);
            setIsLoading(false);
            setPagy(r.data.pagy);
            return r.data;
          })
        );
      } else {
        return (
          shouldFetch &&
          api.get(`/events/all?${queryString}`).then((r) => {
            if (!skipSetEvents) setEvents(r.data);
            setIsLoading(false);
            return r.data;
          })
        );
      }
    },
    [
      api,
      filterData,
      ctmTaxable,
      totalPayroll,
      eventDates,
      sortFilter,
      eventIdsQueryString,
      remittedFilter,
      reportedFilter,
      searchFilter,
      usePagination,
      filterOverrides,
      vendorFilter,
    ]
  );

  useEffect(() => {
    if (Object.keys(eventIds).length > 0) {
      setEventIdsQueryString(
        Object.keys(eventIds)
          .map((id) => `&event_ids[]=${id}`)
          .join("")
      );
    } else {
      setEventIdsQueryString("");
    }
  }, [eventIds]);

  useEffect(() => {
    setPagy([]);
    setEvents([]);
  }, []);

  useEffect(() => {
    getEvents(currentPage);
  }, [getEvents, currentPage]);

  const resetPagination = () => {
    setEvents([]);
    setPagy([]);
    setCurrentPage(1);
  };

  events.forEach((event) => {
    const [year, month, day] = event.date.split("T")[0].split("-");
    event.formattedDate = `${month}/${day}/${year}`;
  });

  const eventDateOptions = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "This month", value: "this_month" },
    { label: "Last month", value: "last_month" },
    { label: "This year", value: "this_year" },
    { label: "Last year", value: "last_year" },
    { label: "First quarter", value: "first_quarter" },
    { label: "Second quarter", value: "second_quarter" },
    { label: "Third quarter", value: "third_quarter" },
    { label: "Fourth quarter", value: "fourth_quarter" },
    { label: "All time", value: "all_time" },
    { label: "Custom", value: "custom" },
  ];

  const date = new Date().toISOString().slice(0, 10);
  const day = new Date().toISOString().slice(8, 10);
  const month = new Date().toISOString().slice(5, 7);
  const year = new Date().toISOString().slice(0, 4);

  const handleEventDateSelect = (e) => {
    switch (e.target?.id || e.value) {
      case "today":
        return setEventDates({
          dateButton: "today",
          startDate: date,
          endDate: date,
        });
      case "yesterday":
        if (day === "01") {
          if (month === "01") {
            return setEventDates({
              dateButton: "yesterday",
              startDate: `${year - 1}-12-31`,
              endDate: `${year - 1}-12-31`,
            });
          } else if (month === "02" || month === "04" || month === "06" || month === "08" || month === "09") {
            return setEventDates({
              dateButton: "yesterday",
              startDate: `${year}-0${month - 1}-31`,
              endDate: `${year}-0${month - 1}-31`,
            });
          } else if (month === "11") {
            return setEventDates({
              dateButton: "yesterday",
              startDate: `${year}-${month - 1}-31`,
              endDate: `${year}-${month - 1}-31`,
            });
          } else if (month === "05" || month === "07" || month === "10") {
            return setEventDates({
              dateButton: "yesterday",
              startDate: `${year}-0${month - 1}-30`,
              endDate: `${year}-0${month - 1}-30`,
            });
          } else if (month === "12") {
            return setEventDates({
              dateButton: "yesterday",
              startDate: `${year}-${month - 1}-30`,
              endDate: `${year}-${month - 1}-30`,
            });
          } else if (month === "03" && year % 4 === 0) {
            return setEventDates({
              dateButton: "yesterday",
              startDate: `${year}-02-29`,
              endDate: `${year}-02-29`,
            });
          } else if (month === "03" && year % 4 !== 0) {
            return setEventDates({
              dateButton: "yesterday",
              startDate: `${year}-02-28`,
              endDate: `${year}-02-28`,
            });
          }
        } else {
          return setEventDates({
            dateButton: "yesterday",
            startDate: `${year}-${month}-${day - 1}`,
            endDate: `${year}-${month}-${day - 1}`,
          });
        }
        break;
      case "this_month":
        if (
          month === "01" ||
          month === "03" ||
          month === "05" ||
          month === "07" ||
          month === "08" ||
          month === "10" ||
          month === "12"
        ) {
          return setEventDates({
            dateButton: "this_month",
            startDate: `${year}-${month}-01`,
            endDate: `${year}-${month}-31`,
          });
        } else if (month === "04" || month === "06" || month === "09" || month === "11") {
          return setEventDates({
            dateButton: "this_month",
            startDate: `${year}-${month}-01`,
            endDate: `${year}-${month}-30`,
          });
        } else if (month === "02" && year % 4 === 0) {
          return setEventDates({
            dateButton: "this_month",
            startDate: `${year}-02-01`,
            endDate: `${year}-02-29`,
          });
        } else if (month === "02" && year % 4 !== 0) {
          return setEventDates({
            dateButton: "this_month",
            startDate: `${year}-02-01`,
            endDate: `${year}-02-28`,
          });
        }
        break;
      case "last_month":
        if (month === "01") {
          return setEventDates({
            dateButton: "last_month",
            startDate: `${year - 1}-12-01`,
            endDate: `${year - 1}-12-31`,
          });
        } else if (month === "02" || month === "04" || month === "06" || month === "08" || month === "09") {
          return setEventDates({
            dateButton: "last_month",
            startDate: `${year}-0${month - 1}-01`,
            endDate: `${year}-0${month - 1}-31`,
          });
        } else if (month === "11") {
          return setEventDates({
            dateButton: "last_month",
            startDate: `${year}-${month - 1}-01`,
            endDate: `${year}-${month - 1}-31`,
          });
        } else if (month === "05" || month === "07" || month === "10") {
          return setEventDates({
            dateButton: "last_month",
            startDate: `${year}-0${month - 1}-01`,
            endDate: `${year}-0${month - 1}-30`,
          });
        } else if (month === "12") {
          return setEventDates({
            dateButton: "last_month",
            startDate: `${year}-${month - 1}-01`,
            endDate: `${year}-${month - 1}-30`,
          });
        } else if (month === "03" && year % 4 === 0) {
          return setEventDates({
            dateButton: "last_month",
            startDate: `${year}-02-01`,
            endDate: `${year}-02-29`,
          });
        } else if (month === "03" && year % 4 !== 0) {
          return setEventDates({
            dateButton: "last_month",
            startDate: `${year}-02-01`,
            endDate: `${year}-02-28`,
          });
        }
        break;
      case "this_year":
        return setEventDates({
          dateButton: "this_year",
          startDate: `${year}-01-01`,
          endDate: `${year}-12-31`,
        });
      case "last_year":
        return setEventDates({
          dateButton: "last_year",
          startDate: `${year - 1}-01-01`,
          endDate: `${year - 1}-12-31`,
        });
      case "first_quarter":
        return setEventDates({
          dateButton: "first_quarter",
          startDate: `${year}-01-01`,
          endDate: `${year}-03-31`,
        });
      case "second_quarter":
        return setEventDates({
          dateButton: "second_quarter",
          startDate: `${year}-04-01`,
          endDate: `${year}-06-30`,
        });
      case "third_quarter":
        return setEventDates({
          dateButton: "third_quarter",
          startDate: `${year}-07-01`,
          endDate: `${year}-09-30`,
        });
      case "fourth_quarter":
        return setEventDates({
          dateButton: "fourth_quarter",
          startDate: `${year}-10-01`,
          endDate: `${year}-12-31`,
        });
      case "all_time":
        return setEventDates({
          dateButton: "all_time",
          startDate: `1900-01-01`,
          endDate: `2100-12-31`,
        });
      case "custom":
        return setEventDates({
          dateButton: "custom",
          startDate: "",
          endDate: "",
        });
      default:
    }
  };

  const handleCustomEventStartDate = (e) => {
    eventDates.dateButton === "custom" &&
      setEventDates({
        ...eventDates,
        startDate: e.target.value,
      });
  };

  const handleCustomEventEndDate = (e) => {
    eventDates.dateButton === "custom" &&
      setEventDates({
        ...eventDates,
        endDate: e.target.value,
      });
  };

  const renderCustomEventDateSelector = () => {
    return (
      <div className={eventDates.dateButton === "custom" ? "flex text-xs text-black" : "flex text-xs text-gray-400"}>
        <div className="flex flex-col w-1/2 mr-2">
          <label htmlFor="startDate">From</label>
          <input
            value={eventDates.startDate ?? ""}
            disabled={eventDates.dateButton !== "custom"}
            type="date"
            id="startDate"
            className="h-10 pl-2 rounded-md border"
            onChange={handleCustomEventStartDate}
          ></input>
        </div>
        <div className="flex flex-col w-1/2">
          <label htmlFor="endDate">To</label>
          <input
            value={eventDates.endDate ?? ""}
            disabled={eventDates.dateButton !== "custom"}
            type="date"
            id="endDate"
            className="h-10 pl-2 rounded-md border"
            onChange={handleCustomEventEndDate}
          ></input>
        </div>
      </div>
    );
  };

  return {
    getEvents,
    events,
    pagy,
    currentPage,
    setCurrentPage,
    resetPagination,
    eventDates,
    eventDateOptions,
    handleEventDateSelect,
    renderCustomEventDateSelector,
    remittedFilter,
    setRemittedFilter,
    reportedFilter,
    setReportedFilter,
    sortFilter,
    setSortFilter,
    searchFilter,
    isLoading,
    setIsLoading,
    setSearchFilter,
    eventIds,
    setEventIds,
  };
};

export default useEvents;
